import { Box, Theme } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { FC, useEffect, useRef, useState } from 'react';
import 'react-multi-carousel/lib/styles.css';
import { SliderArrow } from 'components/structures/about-us';
import Image from 'blog/components/Image';
import clsx from 'clsx';

interface SlideProps {
  image: string;
  isHorizontalImg?: boolean;
}

interface SliderProps {
  sliderList: Array<SlideProps>;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    viewport: {
      overflow: 'hidden',
      width: '100%',
      marginBottom: 50,
    },

    track: {
      display: 'flex',
      gap: 12,
      transition: 'transform 0.6s ease',
      willChange: 'transform',
      [theme.breakpoints.up('sm')]: {
        gap: 32,
      },
    },

    slide: {
      flexShrink: 0,
      height: 450,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },

    square: {
      width: 338, // 450 * 0.75
    },

    horizontal: {
      width: 506, // 675 * 0.75
    },
  }),
);

const SPEED = 0.09;

const CompanySlider: FC<SliderProps> = ({ sliderList }) => {
  const classes = useStyles();
  const trackRef = useRef<HTMLDivElement>(null);

  const [paused, setPaused] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [sliderList[sliderList.length - 1], ...sliderList, sliderList[0]];

  const position = useRef(0);
  const lastTime = useRef<number | null>(null);
  const rafId = useRef<number | null>(null);

  const getCurrentSlideWidth = () => (sliderList[currentSlide]?.isHorizontalImg ? 506 : 338) + 24; // учитываем gap
  const getSlideWidth = (i: number) => (slides[i]?.isHorizontalImg ? 506 : 338) + 24;

  const totalWidth = slides.reduce((acc, _, i) => acc + getSlideWidth(i), 0);

  const animate = (time: number) => {
    if (!lastTime.current) lastTime.current = time;
    const delta = time - lastTime.current;
    lastTime.current = time;

    if (!paused) {
      position.current += delta * SPEED;
    }

    if (position.current >= totalWidth - getSlideWidth(slides.length - 1)) {
      position.current = getSlideWidth(0);
    }

    if (trackRef.current) {
      trackRef.current.style.transform = `translateX(-${position.current}px)`;
    }

    rafId.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    rafId.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId.current!);
  }, [paused]);

  const scrollToSlide = (dir: 1 | -1) => {
    setPaused(true);

    const slideWidth = getCurrentSlideWidth();

    position.current += dir * slideWidth;

    setCurrentSlide((prev) => (prev + dir + sliderList.length) % sliderList.length);
    setTimeout(() => {
      setPaused(false);
    }, 400);
  };

  return (
    <Box onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <Box className={classes.viewport}>
        <div ref={trackRef} className={classes.track} style={{ transition: 'none' }}>
          {slides.map(({ image, isHorizontalImg }, i) => (
            <Box key={i} className={clsx(classes.slide, isHorizontalImg ? classes.horizontal : classes.square)}>
              <Image src={image} alt={image} width={isHorizontalImg ? 506 : 338} height={338} style={{ objectFit: 'cover' }} />
            </Box>
          ))}
        </div>
      </Box>

      <SliderArrow
        totalSlides={sliderList.map((_, i) => i)}
        currentSlide={currentSlide}
        next={() => scrollToSlide(1)}
        prev={() => scrollToSlide(-1)}
      />
    </Box>
  );
};

export default CompanySlider;
